"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Calendar, Home, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
function PostContentConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [slug, setSlug] = useState<string | null>(null);

  // 使用 useEffect 在客戶端渲染後獲取網址參數，避免伺服器端報錯
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      setSlug(params.get("slug"));
    }
  }, []);
  if (loading || slug === null) {
    return <div className="max-w-[42rem] mx-auto px-4 py-16 animate-pulse">
        <div className="h-10 w-64 bg-muted rounded-full mb-8"></div>
        <div className="h-16 w-full bg-muted rounded-md mb-6"></div>
      </div>;
  }

  // 精準匹配該 slug 的文章
  const post = posts?.find((p: any) => p.slug === slug);
  if (error || !post) {
    return <div className="max-w-[42rem] mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">抱歉，找不到該篇文章內容。</p>
        <p className="text-xs text-muted-foreground/50 mt-2">ID: {slug || "none"}</p>
        <Link to="/Knowledge" className="text-primary mt-4 block underline">返回百科列表</Link>
      </div>;
  }
  const primaryCategory = post.categories && post.categories.length > 0 ? post.categories[0] : null;
  const formattedDate = post.date ? new Date(post.date).toLocaleDateString("zh-TW") : "";
  return <article className="max-w-[42rem] mx-auto px-4 w-full">
      <div className="mb-10">
        <Breadcrumb className="inline-flex bg-accent/50 px-4 py-2 rounded-full border border-border shadow-sm">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/HomePage"><Home className="w-4 h-4 text-primary" /></Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium text-primary line-clamp-1">{post.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <h1 className="font-semibold text-4xl md:text-6xl leading-[1.1] mb-8 text-foreground" dangerouslySetInnerHTML={{
      __html: post.title
    }} />

      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-16">
        <SafeHydrate><Calendar className="w-4 h-4 text-primary" /></SafeHydrate>
        <time>{formattedDate}</time>
        {post.author && <>
            <Separator orientation="vertical" className="h-4" />
            <User className="w-4 h-4" />
            <span>{post.author.name}</span>
          </>}
      </div>

      <div className="prose prose-lg leading-[1.8] text-foreground
          [&_p]:mb-8 [&_h2]:text-3xl [&_h2]:text-primary [&_h2]:mt-12 [&_h2]:mb-6
          [&_img]:rounded-xl [&_img]:shadow-lg [&_img]:my-10" dangerouslySetInnerHTML={{
      __html: post.content || ""
    }} />
    </article>;
}
export default function PostContent() {
  return <section id="article-content" className="bg-background text-foreground py-16 md:py-32 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      
      {/* 抓取足夠的文章量供匹配 */}
      <PostsProvider postsPerPage={100}>
        <PostContentConsumer />
      </PostsProvider>
    </section>;
}